import { useCallback } from 'react';

import { Box, Stack } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { Card, Link, Avatar, Tooltip, Checkbox, Typography, CardContent, useMediaQuery } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { ICompanyProfile, CompanyProfileType } from 'src/services/connections/connections.types';

import Image from 'src/components/image';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label/label';
import DateDisplay from 'src/components/date/date-display';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';


// ----------------------------------------------------------------------
type Props = {
  row: ICompanyProfile;
  selected?: boolean;
  onSelectRow?: VoidFunction;
  onRemoveItem?: VoidFunction;
};

export default function ListItemCompanyTableRow({
  row,
  selected,
  onSelectRow,
  onRemoveItem
}: Props) {

  const theme = useTheme();

  const tenantType = useOrgTenant();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const popover = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const { target_tenant } = useParams();

  const getViewDetailPath = useCallback((data: ICompanyProfile) => paths.dashboard.companies.detail(data.id, tenantType, data.company_type), [tenantType]);

  const viewDetail = useCallback((data: ICompanyProfile) => {
    router.push(getViewDetailPath(data));
  }, [router, getViewDetailPath]);

  const onEditCompany = useCallback(() => {
    if (!row || row.type !== CompanyProfileType.UNLINKED) return;

    dispatch(showCompanyEditor(row.id));

  }, [dispatch, row]);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent sx={{ position: 'relative', pr: 6, cursor: 'pointer' }} >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ pt: '.5em' }}>
              {
                row.company_logo ? (
                  <Image src={row.company_logo.public_path} alt={row.company_name} sx={{ width: 25, height: 25, borderRadius: 1 }} />
                )
                  :
                  (
                    <Avatar sx={{ width: 25, height: 25, bgcolor: theme.palette.primary.main, fontSize: 13 }}>
                      {row.company_name.charAt(0)}
                    </Avatar>
                  )
              }
            </Box>
            <Stack direction="column" alignItems="start">
              <Link
                href={getViewDetailPath(row)}
                style={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                }}
              >
                <TruncatedTextField
                  text={row.company_name}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="caption"
                  fontWeight="bold"
                  gutterBottom
                  style={{ textTransform: 'capitalize' }}
                />
              </Link>
            </Stack>
          </Stack>

          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 50 }}
            color={popover.open ? 'primary' : 'default'}
            onClick={popover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
          sx={{ width: 140 }}
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              viewDetail(row);
            }}
          >
            <Iconify icon="solar:eye-bold" />
            {t('common.view')}
          </MenuItem>

          <MenuItem
            onClick={onRemoveItem}
          >
            <Iconify icon="ix:remove-circle-filled" color="red" />
            {t('lists.actions.remove_from_list')}
          </MenuItem>

        </CustomPopover>
      </Card>
    );
  }

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" alignItems="center" justifyContent="start" spacing={.5}>
            <Link
              component={RouterLink}
              noWrap
              color="inherit"
              href={getViewDetailPath(row)}
            >
              <TruncatedTextField
                text={row.company_name}
                mode="characters"
                limit={50}
                showMoreEnabled={false}
                variant="subtitle2"
                fontWeight="bold"
                style={{ textTransform: 'capitalize' }}
              />
            </Link>
          </Stack>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Label variant="soft">{t(`connections.table.company_type.${row.company_type}`)}</Label>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.company_phone || t('common.not_available')}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.account_owner ? `${row.account_owner?.first_name} ${row.account_owner?.last_name}` : '--'}
          </Typography>
        </TableCell >

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {row.primary_address ? `${row.primary_address?.city}, ${row.primary_address?.state}` : '--'}
          </Typography>
        </TableCell >

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {
            row.thread?.last_activity_at ? (
              <DateDisplay date={row.thread?.last_activity_at} format="DD/MM/YYYY hh:mm A" variant='body2' />
            )
              :
              <Typography variant="body2" color="text.secondary">
                --
              </Typography>
          }
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Tooltip title={t('common.actions')}>
            <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
              <Iconify icon="eva:more-horizontal-fill" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow >

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            viewDetail(row);
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>

        {
          row.type === CompanyProfileType.UNLINKED && (
            <MenuItem
              onClick={() => {
                popover.onClose();
                onEditCompany();
              }}
            >
              <Iconify icon="material-symbols:edit" />
              {t('common.edit')}
            </MenuItem>
          )
        }

        <MenuItem
          onClick={onRemoveItem}
        >
          <Iconify icon="ix:remove-circle-filled" color="red" />
          {t('lists.actions.remove_from_list')}
        </MenuItem>


      </CustomPopover>
    </>
  );
}